import { EyeOutlined } from '@ant-design/icons'
import { Grid, Modal, Spin } from 'antd'
import { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { apiGetTestCaseDetail } from '../../../../../redux/testRun/testRun.service'
import { testRunTestCaseDetail } from '../../../../../redux/testRun/testRunList.slice'
import StatusPill from '../../../../shared/StatusPill'
import TagChipsCell from '../../../../shared/TagChipsCell'
import { isValidObject } from '../../../../../utils/helpers'
import styles from './AddTestCaseModal.module.css'

function ReadOnlyField({ label, value, multiline = false, className = '' }) {
    const text = typeof value === 'string' ? value.trim() : value
    const isEmpty = text === undefined || text === null || text === ''

    return (
        <div className={className}>
            <div className={styles.readOnlyLabel}>{label}</div>
            <div
                className={`${styles.readOnlyField} ${
                    multiline ? styles.readOnlyFieldMultiline : ''
                }`}
            >
                {isEmpty ? (
                    <span className={styles.readOnlyEmpty}>—</span>
                ) : (
                    text
                )}
            </div>
        </div>
    )
}

const ViewTestCaseModal = ({
    isOpen = false,
    onClose = () => {},
    testCaseId = '',
    reportId = '',
}) => {
    const dispatch = useDispatch()
    const screens = Grid.useBreakpoint()
    const modalWidth = screens.lg ? 780 : screens.md ? 680 : '94%'

    const [loading, setLoading] = useState(false)
    const [detail, setDetail] = useState(null)

    useEffect(() => {
        if (!isOpen || !testCaseId) return

        let cancelled = false
        ;(async () => {
            setLoading(true)
            setDetail(null)
            try {
                if (reportId) {
                    const resp = await dispatch(
                        testRunTestCaseDetail({ reportId, testCaseId })
                    )
                    if (!cancelled) {
                        setDetail(resp?.payload?.data || null)
                    }
                } else {
                    const res = await apiGetTestCaseDetail(testCaseId)
                    if (!cancelled) {
                        setDetail(res?.data || null)
                    }
                }
            } finally {
                if (!cancelled) setLoading(false)
            }
        })()

        return () => {
            cancelled = true
        }
    }, [isOpen, testCaseId, reportId, dispatch])

    const data = isValidObject(detail) ? detail : {}

    return (
        <Modal
            className={styles.modal}
            open={isOpen}
            onCancel={onClose}
            destroyOnClose
            width={modalWidth}
            centered
            maskClosable={!loading}
            closable={!loading}
            title={
                <div className={styles.header}>
                    <div className={styles.headerIcon}>
                        <EyeOutlined />
                    </div>
                    <div className={styles.headerText}>
                        <h2 className={styles.modalTitle}>View Test Case</h2>
                        <p className={styles.subtitle}>
                            Review the test scenario, steps, and organization
                            for this case.
                        </p>
                    </div>
                </div>
            }
            footer={
                <div className={styles.footerBar}>
                    <span className={styles.footerHint}>
                        {data.testId ? `ID: ${data.testId}` : 'Read-only view'}
                    </span>
                    <div className={styles.footerActions}>
                        <button
                            type="button"
                            className={styles.cancelButton}
                            onClick={onClose}
                            disabled={loading}
                        >
                            Close
                        </button>
                    </div>
                </div>
            }
        >
            {loading ? (
                <div className={styles.loadingWrap}>
                    <Spin />
                </div>
            ) : (
                <div className={styles.sections}>
                    <section className={styles.sectionCard}>
                        <div className={styles.sectionCardHeader}>
                            <p className={styles.sectionEyebrow}>Step 1</p>
                            <h3 className={styles.sectionTitle}>Overview</h3>
                        </div>
                        {data.status && (
                            <div className={styles.viewMetaRow}>
                                <StatusPill status={data.status} />
                            </div>
                        )}
                        <div className={styles.formGrid}>
                            <ReadOnlyField
                                label="Title"
                                value={data.title}
                                className={styles.formGridFull}
                            />
                            <ReadOnlyField
                                label="Description"
                                value={data.description}
                                multiline
                                className={styles.formGridFull}
                            />
                        </div>
                    </section>

                    <section className={styles.sectionCard}>
                        <div className={styles.sectionCardHeader}>
                            <p className={styles.sectionEyebrow}>Step 2</p>
                            <h3 className={styles.sectionTitle}>
                                Test specification
                            </h3>
                        </div>
                        <div className={styles.formGrid}>
                            <ReadOnlyField
                                label="Preconditions"
                                value={data.preconditions}
                                multiline
                                className={styles.formGridFull}
                            />
                            <ReadOnlyField
                                label="Test data"
                                value={data.testData}
                                multiline
                            />
                            <ReadOnlyField
                                label="Steps"
                                value={data.steps}
                                multiline
                            />
                            <ReadOnlyField
                                label="Expected result"
                                value={data.expectedResult}
                                multiline
                            />
                        </div>
                    </section>

                    <section className={styles.sectionCard}>
                        <div className={styles.sectionCardHeader}>
                            <p className={styles.sectionEyebrow}>Step 3</p>
                            <h3 className={styles.sectionTitle}>
                                Organization
                            </h3>
                        </div>
                        <div className={styles.formGrid}>
                            <ReadOnlyField
                                label="Sprint"
                                value={data.sprint?.title}
                            />
                            <ReadOnlyField
                                label="Epic"
                                value={data.epic?.title}
                            />
                            <div className={styles.formGridFull}>
                                <div className={styles.readOnlyLabel}>Tags</div>
                                <div className={styles.readOnlyTagsBox}>
                                    <TagChipsCell tags={data.tags} wrap />
                                </div>
                            </div>
                        </div>
                    </section>
                </div>
            )}
        </Modal>
    )
}

export default ViewTestCaseModal
